import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { connect } from 'react-redux';
import { useLocation } from 'react-router-dom';

import {
    Box,
    IconButton,
    Typography,
    Link,
    Divider,
    useTheme,
    useMediaQuery,
    Grid,
} from '@mui/material';
import clsx from 'clsx';
import BillingMovedBanner from 'components/BillingMovedBanner';
import FeatureAnnouncement from 'components/FeatureAnnouncement';
import SandboxBanner from 'components/SandboxBanner';
import Sidebar from 'components/V2/Sidebar';
import { IS_SANDBOX_ENV } from 'helper/constant';
import { getAdminDetails, getInitials, truncateUserName } from 'helper/utility';
import useHubSpotChat from 'hooks/useHubSpotChat';
import PropTypes from 'prop-types';
import endPoints from 'route/endpoints';
import PaymentInfoModal from 'views/AddPaymentMethodModal/PaymentInfoModal';

import { fetchAdminInformationAction } from 'redux/actions/auth';

import styles from './dashboard.module.css';

import { ReactComponent as FileIcon } from 'asset/IconsPack/at_file-06.svg';
import { ReactComponent as MenuIcon } from 'asset/IconsPack/at_menu-03-filled.svg';

import SwitchEnvironment from './components/SwitchEnvironment';
import UserSettings from './components/UserSettings';

function Dashboard({
    children,
    user,
    adminData,
    mainContainerStyles,
    fetchAdminInformationAction,
    token,
}) {
    const location = useLocation();
    const theme = useTheme();

    const [openSidebar, setOpenSidebar] = useState(false);
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const isTablet = useMediaQuery(theme.breakpoints.down('lg'));

    const { firstName, lastName } = getAdminDetails(user?.company, adminData);

    const nameInitials = getInitials(`${firstName} ${lastName}`);

    const truncatedFirstName = truncateUserName(firstName, 24);

    useEffect(() => {
        if (!adminData) {
            fetchAdminInformationAction(token);
        }
    }, [fetchAdminInformationAction, adminData, token]);

    const handleToggler = () => {
        setOpenSidebar(!openSidebar);
    };

    const { openChatBot } = useHubSpotChat();

    const handleHelpClick = () => {
        openChatBot();
    };

    return (
        <>
            <Helmet>
                {!isMobile && (
                    <script
                        type="text/javascript"
                        id="hs-script-loader"
                        async
                        defer
                        src="//js-na1.hs-scripts.com/6431620.js"
                    ></script>
                )}
            </Helmet>
            <Box className={styles.dashboardContainer}>
                <Sidebar
                    isMobile={isMobile}
                    isTablet={isTablet}
                    openSidebar={openSidebar}
                    handleToggler={handleToggler}
                    firstName={truncatedFirstName}
                />
                <Grid className={styles.dashboardInnerContent}>
                    <Box className={styles.navigationContainer}>
                        <Box className={styles.companyInfo}>
                            {isTablet && (
                                <IconButton
                                    className={styles.menuToggleBtn}
                                    onClick={handleToggler}
                                >
                                    <MenuIcon className={styles.menuIcon} />
                                </IconButton>
                            )}
                            <Typography className={styles.companyName}>
                                {truncateUserName(user.company.company_name, 24)}
                            </Typography>
                        </Box>
                        <Box className={styles.userInfoSection}>
                            <SwitchEnvironment />
                            {!isMobile && (
                                <>
                                    <Divider
                                        orientation="vertical"
                                        flexItem
                                        className={styles.divider}
                                    />
                                    <Link
                                        href={
                                            IS_SANDBOX_ENV
                                                ? 'https://info.authenticate.com/sandbox'
                                                : 'https://docs.authenticate.com/'
                                        }
                                        target="_blank"
                                        rel="noreferrer"
                                        className={styles.docLink}
                                    >
                                        <FileIcon className={styles.fileIcon} />{' '}
                                        <span>Technical Docs</span>
                                    </Link>

                                    <Divider
                                        orientation="vertical"
                                        flexItem
                                        className={styles.divider}
                                    />
                                    <UserSettings
                                        firstName={truncatedFirstName}
                                        handleHelpClick={handleHelpClick}
                                        nameInitials={nameInitials}
                                    />
                                </>
                            )}
                        </Box>
                    </Box>
                    <SandboxBanner />
                    {location.pathname === endPoints.dashboard && <BillingMovedBanner />}
                    <Box className={clsx(mainContainerStyles, styles.renderScreenContainer)}>
                        <Box className={styles.innerScreenContainer}>{children}</Box>
                    </Box>
                </Grid>
            </Box>
            <PaymentInfoModal />
            <FeatureAnnouncement isMobile={isMobile} />
        </>
    );
}

Dashboard.propTypes = {
    user: PropTypes.object.isRequired,
    children: PropTypes.node,
    adminData: PropTypes.object,
    mainContainerStyles: PropTypes.string,
    token: PropTypes.string,
    fetchAdminInformationAction: PropTypes.func,
};

function mapStateToProps(state) {
    return {
        user: state.auth?.userData?.user,
        adminData: state.auth?.adminData,
        token: state.auth?.userData.token,
    };
}

export default connect(mapStateToProps, { fetchAdminInformationAction })(Dashboard);
